<template>
  <div class="login">
    <div class="login__halo" aria-hidden="true"></div>

    <div class="login__inner">
      <div class="login__brand">
        <h1 class="login__wordmark">pmm</h1>
        <p class="login__tagline">Digital Ledger — Editorial Financial Intelligence</p>
      </div>

      <div class="al-card login__card">
        <template v-if="sent">
          <div class="forgot__success">
            <span class="material-symbols-outlined forgot__success-icon">mark_email_read</span>
            <h2 class="forgot__title">Check your inbox</h2>
            <p class="forgot__subtitle">
              If that email address is registered, you'll receive a reset link shortly.
              The link expires in 1 hour.
            </p>
            <router-link to="/login" class="forgot__back-link">Back to login</router-link>
          </div>
        </template>

        <template v-else>
          <div class="forgot__header">
            <h2 class="forgot__title">Forgot password</h2>
            <p class="forgot__subtitle">
              Enter your email address and we'll send you a reset link.
            </p>
          </div>

          <n-form @submit.prevent="handleSubmit">
            <n-form-item label="Email">
              <n-input
                v-model:value="email"
                type="email"
                placeholder="you@example.com"
                size="large"
                @keyup.enter="handleSubmit"
              />
            </n-form-item>
            <n-button
              type="primary"
              block
              size="large"
              :loading="authStore.loading"
              @click="handleSubmit"
            >
              Send reset link
            </n-button>
          </n-form>

          <div class="forgot__footer">
            <router-link to="/login" class="forgot__back-link">Back to login</router-link>
          </div>

          <n-alert
            v-if="errorMessage"
            type="error"
            style="margin-top: 1rem"
            closable
            @close="errorMessage = null"
          >
            {{ errorMessage }}
          </n-alert>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { NForm, NFormItem, NInput, NButton, NAlert } from 'naive-ui';
import { useAuthStore } from '@/stores/authStore';

const authStore = useAuthStore();
const email = ref('');
const sent = ref(false);
const errorMessage = ref(null);

async function handleSubmit() {
  if (!email.value) return;
  errorMessage.value = null;
  try {
    await authStore.forgotPassword(email.value);
    sent.value = true;
  } catch (error) {
    errorMessage.value = error.message;
  }
}
</script>

<style scoped>
.login {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  background: var(--al-background);
}

.login__halo {
  position: absolute;
  inset: -20%;
  background:
    radial-gradient(circle at 20% 30%, rgba(0, 229, 255, 0.14), transparent 55%),
    radial-gradient(circle at 80% 70%, rgba(195, 245, 255, 0.07), transparent 50%);
  filter: blur(40px);
  pointer-events: none;
}

.login__inner {
  position: relative;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.login__brand {
  text-align: center;
}

.login__wordmark {
  font-family: var(--al-font-headline);
  font-weight: 800;
  font-size: 2.75rem;
  letter-spacing: 0.03em;
  color: var(--al-primary);
  margin: 0;
}

.login__tagline {
  color: var(--al-on-surface-variant);
  font-size: 0.8125rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0.5rem 0 0;
}

.login__card {
  padding: 2rem;
}

.forgot__header {
  margin-bottom: 1.5rem;
}

.forgot__title {
  font-family: var(--al-font-headline);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: var(--al-on-surface);
}

.forgot__subtitle {
  font-size: 0.875rem;
  color: var(--al-on-surface-variant);
  margin: 0;
  line-height: 1.5;
}

.forgot__footer {
  text-align: center;
  margin-top: 1.25rem;
}

.forgot__back-link {
  font-size: 0.8125rem;
  color: var(--al-on-surface-variant);
  text-decoration: none;
}

.forgot__back-link:hover {
  color: var(--al-primary);
}

.forgot__success {
  text-align: center;
}

.forgot__success-icon {
  font-size: 3rem;
  color: var(--al-primary);
}
</style>
